/**
 * Shared form / surface classes — single source for field size and theme colours.
 * Prefer these over ad-hoc gray/white utilities so light/dark stay consistent.
 */

/** Label above a field */
export const formLabelClass = 'block text-sm font-medium text-ink-muted';

/**
 * Control chrome only (no margin). Fixed height (h-10) matches ShadedSelect `md`.
 */
export const formControlClass =
  'box-border block h-10 w-full rounded-lg border border-edge-strong bg-surface-muted px-3 text-sm text-ink shadow-sm placeholder:text-ink-subtle focus:outline-none focus:ring-2 focus:ring-ring focus:border-edge disabled:cursor-not-allowed disabled:opacity-50';

/**
 * Standard text/email/tel/password input with top margin under a label.
 */
export const formInputClass = `mt-1 ${formControlClass}`;

/** Read-only display that aligns with formInputClass */
export const formReadonlyClass =
  'mt-1 flex h-10 w-full items-center rounded-lg border border-edge bg-surface-muted px-3 text-sm text-ink';

/** Textarea control chrome only (no margin) */
export const formControlTextareaClass =
  'box-border block w-full min-h-[6rem] rounded-lg border border-edge-strong bg-surface-muted px-3 py-2 text-sm text-ink shadow-sm placeholder:text-ink-subtle focus:outline-none focus:ring-2 focus:ring-ring focus:border-edge disabled:cursor-not-allowed disabled:opacity-50';

/** Textarea with top margin under a label */
export const formTextareaClass = `mt-1 ${formControlTextareaClass}`;

export const formPrimaryButtonClass =
  'inline-flex items-center justify-center rounded-full border border-transparent bg-primary px-4 py-2 text-sm font-medium text-primary-foreground shadow-sm hover:bg-primary-hover focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-surface disabled:cursor-not-allowed disabled:opacity-50';

export const formSecondaryButtonClass =
  'inline-flex items-center justify-center rounded-full border border-edge-strong bg-surface px-4 py-2 text-sm font-medium text-ink-muted shadow-sm hover:bg-surface-muted focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-surface disabled:cursor-not-allowed disabled:opacity-50';

/** Solid brand button (always light text on primary fill — never use text-ink-inverse here) */
export const formBrandButtonClass =
  'inline-flex items-center justify-center rounded-md border border-transparent bg-primary px-4 py-2 text-sm font-medium text-primary-foreground shadow-sm hover:bg-primary-hover focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-surface disabled:cursor-not-allowed disabled:opacity-50';

/** Warning / archive outline — matched border + text in both themes */
export const formWarningOutlineButtonClass =
  'inline-flex items-center justify-center rounded-md border border-amber-400 bg-surface px-3 py-2 text-sm font-medium text-amber-800 hover:bg-amber-50 focus:outline-none focus:ring-2 focus:ring-amber-400/40 focus:ring-offset-2 focus:ring-offset-surface dark:border-amber-500 dark:text-amber-200 dark:hover:bg-amber-950/40';

/** Page background */
export const pageCanvasClass = 'bg-canvas text-ink';

/** Standard content card / panel */
export const surfaceCardClass = 'bg-surface border border-edge rounded-lg shadow-sm';

/** Muted inset panel */
export const surfaceMutedClass = 'bg-surface-muted border border-edge rounded-lg';

/** Alert banners */
export const formErrorBannerClass =
  'rounded border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600 dark:border-red-800/60 dark:bg-red-950/40 dark:text-red-300';

export const formSuccessBannerClass =
  'rounded border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-600 dark:border-green-800/60 dark:bg-green-950/40 dark:text-green-300';
